import React, { useEffect, useState, useCallback } from "react";
import { Routes, Route } from "react-router-dom";
import { useLanguage } from "./LanguageContext";
import Hero from "./components/sections/Hero";
import ProjectDetail from "./pages/ProjectDetail";
// import HudOverlay from "./components/ui/HudOverlay";
import SettingsPanel from "./components/ui/SettingsPanel";
import SettingsButton from "./components/ui/SettingsButton";
import LoadingScreen from "./components/ui/LoadingScreen";
import HomeSections from "./components/layout/HomeSections";
import useDeviceType from "./hooks/useDeviceType";
import usePointerAndScroll from "./hooks/usePointerAndScroll";
import { APP_TITLE, LOADING_DURATION } from "./utils/constants";

const App = () => {
  const { isMobile } = useDeviceType();
  const { mousePosition } = usePointerAndScroll(isMobile);
  const [showHero, setShowHero] = useState(true);
  const { language, toggleLanguage } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);
  const [theme, setTheme] = useState("dark");

  // Initialize app and set title
  useEffect(() => {
    document.title = APP_TITLE;

    const timer = setTimeout(() => {
      setLoading(false);
    }, LOADING_DURATION);

    return () => clearTimeout(timer);
  }, []);

  const toggleSettingsPanel = useCallback(() => {
    setSettingsPanelOpen((prev) => !prev);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((prevTheme) => (prevTheme === "dark" ? "light" : "dark"));
  }, []);

  const closeSettings = useCallback(() => {
    setSettingsPanelOpen(false);
  }, []);

  // Memoize light position to avoid unnecessary recalculations
  // const lightPosition = useMemo(
  //   () => ({
  //     x: mousePosition.x + scrollPosition.x,
  //     y: mousePosition.y + scrollPosition.y,
  //   }),
  //   [mousePosition.x, mousePosition.y, scrollPosition.x, scrollPosition.y]
  // );

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="leading-relaxed text-gray-400 antialiased selection:bg-cyan-900 selection:text-cyan-100">
      <div id="__next">
        {/* Grid background */}
        <div className="fixed inset-0 grid-plus-background opacity-5 z-0"></div>

        <div className="__variable_20b187 group/spotlight relative z-10">
          {/* Settings Panel Toggle Button */}
          <SettingsButton onClick={toggleSettingsPanel} />

          {/* Settings Panel */}
          <SettingsPanel
            isOpen={settingsPanelOpen}
            onClose={closeSettings}
            language={language}
            toggleLanguage={toggleLanguage}
            theme={theme}
            toggleTheme={toggleTheme}
          />

          {/* HUD scanner effect - follows cursor */}
          {!isMobile && (
            <div
              className="pointer-events-none fixed inset-0 z-30 transition duration-300"
              style={{
                background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(34,211,238,0.10), transparent 60%)`,
              }}
            ></div>
          )}

          <div className="mx-auto min-h-screen max-w-screen-xl px-6 py-12 font-sans md:px-12 md:py-16 lg:py-0">
            <div className="lg:flex lg:justify-between lg:gap-4">
              {showHero && <Hero />}
              <main
                id="content"
                className={`pt-24 lg:py-24 ${
                  showHero ? "lg:w-1/2" : "lg:w-full"
                }`}
              >
                <Routes>
                  <Route
                    path="/"
                    element={<HomeSections setShowHero={setShowHero} />}
                  />
                  <Route
                    path="/projects/:id"
                    element={<ProjectDetail setShowHero={setShowHero} />}
                  />
                </Routes>
              </main>
            </div>
          </div>
        </div>

        {/* HUD overlay component */}
        {/* <HudOverlay /> */}

        {/* CRT Fixed Effects Overlay - Scanlines */}
        <div className="crt-scanlines"></div>

        {/* CRT Fixed Effects Overlay - Vignette */}
        <div className="crt-vignette"></div>

        {/* CRT Fixed Effects Overlay - Barrel Distortion */}
        <div className="crt-barrel-distortion"></div>

        {/* CRT Noise/Grain effect overlay */}
        <div className="noise"></div>

        {/* Custom cursor - only show on desktop */}
        {!isMobile && (
          <>
            <div
              data-cursor="cursor"
              className="custom-cursor"
              style={{
                left: `${mousePosition.x}px`,
                top: `${mousePosition.y}px`,
              }}
            ></div>
            <div
              data-cursor="cursor-dot"
              className="custom-cursor-dot"
              style={{
                left: `${mousePosition.x}px`,
                top: `${mousePosition.y}px`,
              }}
            ></div>
          </>
        )}
      </div>
    </div>
  );
};

export default App;